import { ctaDesc, ctaTitle } from "@/lib/seo";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { THAI_EMBASSIES_ABROAD } from "@/data/thai-embassies-abroad";
import { Building2, MapPin, Phone } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/thai-embassy";

export const Route = createFileRoute("/thai-embassy/")({
  head: () => {
    const title = "สถานทูตไทยในต่างประเทศ 50+ แห่ง · พาสปอร์ต รับรอง มอบอำนาจ | NYC";
    const desc = "รวมข้อมูลสถานเอกอัครราชทูตและสถานกงสุลใหญ่ไทยทั่วโลก — ที่อยู่ เบอร์โทร เวลาทำการ ค่าธรรมเนียม บริการพาสปอร์ต รับรองเอกสาร หนังสือมอบอำนาจ — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";
    return {
      meta: [
        { title: ctaTitle(title, "/thai-embassy", "th") }, { name: "description", content: ctaDesc(desc, "/thai-embassy", "th")},
        { property: "og:title", content: ctaTitle(title, "/thai-embassy", "th")},
        { property: "og:description", content: ctaDesc(desc, "/thai-embassy", "th")},
        { property: "og:url", content: SITE + PATH },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: SITE + PATH },
        { rel: "alternate", hreflang: "th-TH", href: SITE + PATH },
        { rel: "alternate", hreflang: "en", href: SITE + "/en/thai-embassy" },
        { rel: "alternate", hreflang: "x-default", href: SITE + PATH },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "CollectionPage",
          name: title, url: SITE + PATH, inLanguage: "th",
          about: THAI_EMBASSIES_ABROAD.map((e) => ({
            "@type": "GovernmentOffice", name: `สถานทูตไทย ${e.city}`,
            address: e.addressEn, telephone: e.tel,
            url: `${SITE}${PATH}/${e.slug}`,
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "สถานทูตไทยในต่างประเทศ", item: SITE + PATH },
          ],
        }) },
      ],
    };
  },
  component: ThaiEmbassyHub,
});

const REGION_LABEL: Record<string, string> = {
  "asia": "🌏 เอเชีย", "oceania": "🇦🇺 โอเชียเนีย", "europe": "🇪🇺 ยุโรป",
  "americas": "🌎 อเมริกา", "middle-east": "🕌 ตะวันออกกลาง", "africa": "🌍 แอฟริกา",
};

function ThaiEmbassyHub() {
  const grouped = THAI_EMBASSIES_ABROAD.reduce((acc, e) => {
    (acc[e.region] ||= []).push(e);
    return acc;
  }, {} as Record<string, typeof THAI_EMBASSIES_ABROAD>);

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/70">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5">/</span>
            <span className="text-white/90">สถานทูตไทยในต่างประเทศ</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Building2 className="h-3.5 w-3.5" /> Royal Thai Embassy & Consulate Directory
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            สถานทูตไทยในต่างประเทศ · {THAI_EMBASSIES_ABROAD.length}+ แห่งทั่วโลก
          </h1>
          <p className="mt-4 max-w-3xl text-white/85 sm:text-lg">
            ข้อมูลติดต่อสถานเอกอัครราชทูตและสถานกงสุลใหญ่ไทย — พาสปอร์ต หนังสือมอบอำนาจ รับรองเอกสาร จดทะเบียนสมรส หนังสือรับรองโสด บริการฉุกเฉินคนไทยในต่างประเทศ
          </p>
          <p className="mt-3 text-[13px] text-white/60">
            แหล่งข้อมูล: กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — อัพเดต 2026
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
        {Object.entries(grouped).map(([region, list]) => (
          <section key={region} className="mb-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              {REGION_LABEL[region]} · {list.length} แห่ง
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((e) => (
                <Link
                  key={e.slug}
                  to="/thai-embassy/$country"
                  params={{ country: e.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="text-2xl">{e.flag}</div>
                    <div className="text-[10px] font-bold uppercase text-emerald-600">{e.processingDays} วัน</div>
                  </div>
                  <div className="mt-2 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">
                    {e.countryTh}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[12px] text-slate-500">
                    <MapPin className="h-3 w-3" /> {e.city}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[12px] text-slate-500">
                    <Phone className="h-3 w-3" /> {e.tel}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            NYC ช่วยเตรียมเอกสารก่อนยื่นสถานทูตไทยได้อย่างไร
          </h2>
          <p className="mt-3 text-slate-700">
            ก่อนคนไทยในต่างประเทศจะยื่นเอกสารกับสถานทูตไทย มักต้องมีเอกสารต้นทางจากไทยที่ผ่านการรับรองจาก
            กระทรวงการต่างประเทศ (MFA) หรือ Apostille ก่อน เช่น สูติบัตร ทะเบียนบ้าน ทะเบียนสมรส หนังสือรับรองโสด หนังสือมอบอำนาจ
            NYC ให้บริการจัดเตรียมและรับรองเอกสารครบวงจร พร้อมส่ง EMS ไปต่างประเทศ
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <Link to="/mfa-legalisation" className="rounded-full bg-gold px-5 py-2 font-bold text-navy-deep">รับรอง MFA</Link>
            <Link to="/apostille" className="rounded-full border border-navy-deep px-5 py-2 font-bold text-navy-deep">Apostille</Link>
            <Link to="/translate" className="rounded-full border border-navy-deep px-5 py-2 font-bold text-navy-deep">แปลเอกสาร</Link>
            <Link to="/marriage" className="rounded-full border border-navy-deep px-5 py-2 font-bold text-navy-deep">จดทะเบียนสมรส</Link>
          </div>
        </section>
      </div>
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  );
}
